// src/pages/authz/AuthzInspectorPage.tsx
// Authorization inspector — check simulator, batch check, permission
// discovery, tuple browser, and authorization model info.
// See docs/ux/authz-ia.md. Admin-gated (AdminRoute, in App.tsx).

import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { CheckSimulator } from '@/features/authz/CheckSimulator';
import { BatchCheckPanel } from '@/features/authz/BatchCheckPanel';
import { DiscoveryPanel } from '@/features/authz/DiscoveryPanel';
import { TupleBrowser } from '@/features/authz/TupleBrowser';
import { ModelPanel } from '@/features/authz/ModelPanel';

type Tab = 'check' | 'batch' | 'discovery' | 'tuples' | 'model';
const TABS: Tab[] = ['check', 'batch', 'discovery', 'tuples', 'model'];

export const AuthzInspectorPage = observer(() => {
  const { t } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();

  const tab = useMemo<Tab>(() => {
    const v = searchParams.get('tab');
    return (TABS as string[]).includes(v ?? '') ? (v as Tab) : 'check';
  }, [searchParams]);

  return (
    <div className="space-y-4 p-6">
      <div>
        <h1 className="text-xl font-semibold text-foreground">{t('authz.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('authz.subtitle')}</p>
      </div>

      <div role="tablist" aria-label={t('authz.title')} className="flex flex-wrap gap-1 border-b border-border">
        {TABS.map((v) => (
          <button
            key={v}
            type="button"
            role="tab"
            aria-selected={tab === v}
            onClick={() => setSearchParams({ tab: v })}
            className={`-mb-px border-b-2 px-3 py-2 text-sm ${
              tab === v ? 'border-primary font-medium text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {t(`authz.tab.${v}`)}
          </button>
        ))}
      </div>

      {tab === 'check' && <CheckSimulator />}
      {tab === 'batch' && <BatchCheckPanel />}
      {tab === 'discovery' && <DiscoveryPanel />}
      {tab === 'tuples' && <TupleBrowser />}
      {tab === 'model' && <ModelPanel />}
    </div>
  );
});

export default AuthzInspectorPage;
